/* RUEMOOD, ON YOU — scoped photo journal. No customer/review claims or tracking. */
(() => {
  'use strict';
  if (customElements.get('ruemood-photo-journal')) return;
  const rhythm = [[1,.06],[.73,.23],[1.08,0],[.87,.12],[.72,.26],[.96,.03],[.81,.18]];
  class RuemoodPhotoJournal extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      this.$ = selector => this.querySelector(selector);
      this.strip = this.$('[data-rmo-strip]');
      this.track = this.$('[data-rmo-track]');
      this.all = this.$('[data-rmo-wall]');
      this.lightbox = this.$('[data-rmo-lightbox]');
      this.cards = [...this.track.querySelectorAll('[data-rmo-photo]')];
      this.items = this.cards.map(card => ({
        id: card.dataset.photoId, thumbnail: card.dataset.thumbnail,
        image: card.dataset.image, alt: card.querySelector('img').alt,
        closeup: card.dataset.closeup === 'true'
      }));
      this.reduce = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.atRest();
      this.visible = false;
      this.hover = false;
      this.focused = false;
      this.pointing = false;
      this.manualUntil = 0;
      this.cycle = 0;
      this.index = 0;
      this.drag = null;
      this.suppressClick = false;
      this.gridReady = false;
      this.raf = 0;
      this.last = 0;
      this.position = null;
      this.listen = (target, event, handler, options = {}) => target.addEventListener(event, handler, {...options, signal: this.abort.signal});
      this.lazy = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting) { this.load(entry.target); this.lazy.unobserve(entry.target); }
      }), {root: this.strip, rootMargin: '0px 420px'});
      this.cards.forEach((card, index) => this.prepareCard(card, index));
      // A complete second pass also covers ultrawide screens at the loop seam.
      // Clones are hidden from keyboard/assistive navigation and load only nearby.
      this.items.forEach((item, index) => {
        const clone = this.makeCard(item, index);
        clone.tabIndex = -1;
        clone.setAttribute('aria-hidden', 'true');
        clone.dataset.rmoClone = '';
        this.track.append(clone);
      });
      this.resize = new ResizeObserver(() => { this.measure(); this.motion(); });
      this.resize.observe(this.track);
      this.approach = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        this.track.querySelectorAll('img[data-src]').forEach(img => this.lazy.observe(img));
        this.approach.disconnect();
      }, {rootMargin: '400px 0px'});
      this.approach.observe(this);
      this.visibility = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.motion();
      }, {threshold: .05});
      this.visibility.observe(this);
      this.listen(this, 'click', event => this.click(event));
      // Touch browsers fire mouseenter on a tap and never mouseleave, so only a real mouse holds the strip by hovering.
      this.listen(this.strip, 'pointerenter', event => { if (event.pointerType === 'mouse') { this.hover = true; this.motion(); } });
      this.listen(this.strip, 'pointerleave', () => { this.hover = false; this.motion(); });
      // Only keyboard travel holds it by focus. Safari marks focus handed back by script as :focus-visible, so a pointer press rules it out.
      this.listen(document, 'pointerdown', () => { this.pointing = true; this.keyFocus(); }, {capture:true, passive:true});
      this.listen(document, 'keydown', event => { if (event.key === 'Tab') this.pointing = false; }, {capture:true});
      this.listen(this.strip, 'focusin', () => this.keyFocus());
      this.listen(this.strip, 'focusout', () => { this.focused = false; this.motion(); });
      ['wheel', 'touchstart', 'touchend'].forEach(name => this.listen(this.strip, name, () => this.hold(), {passive:true}));
      this.listen(this.strip, 'keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); this.step(event.key === 'ArrowRight' ? 1 : -1);
        }
      });
      this.listen(this.strip, 'pointerdown', event => {
        this.hold();
        if (event.pointerType === 'mouse' && event.button === 0) {
          this.drag = {x:event.clientX, left:this.strip.scrollLeft}; this.suppressClick = false;
        }
      });
      this.listen(window, 'pointermove', event => {
        if (!this.drag) return;
        const delta = event.clientX - this.drag.x;
        if (Math.abs(delta) > 5) {
          this.suppressClick = true; this.strip.classList.add('is-dragging');
          this.strip.scrollLeft = this.drag.left - delta;
        }
      });
      ['pointerup', 'pointercancel', 'blur', 'contextmenu'].forEach(name => this.listen(window, name, () => this.endDrag()));
      this.listen(this.strip, 'scroll', () => {
        if (this.cycle && this.strip.scrollLeft >= this.cycle) this.strip.scrollLeft -= this.cycle;
      }, {passive:true});
      this.listen(document, 'visibilitychange', () => this.motion());
      this.listen(this.reduce, 'change', () => { this.paused = this.atRest(); this.motion(); });
      this.listen(this.lightbox, 'keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); this.advance(event.key === 'ArrowRight' ? 1 : -1);
        }
      });
      const enlarged = this.$('[data-rmo-image]');
      this.listen(enlarged, 'touchstart', event => { this.touch = {x:event.touches[0].clientX,y:event.touches[0].clientY}; }, {passive:true});
      this.listen(enlarged, 'touchend', event => {
        if (this.touch) {
          const dx = event.changedTouches[0].clientX - this.touch.x, dy = event.changedTouches[0].clientY - this.touch.y;
          if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.5) this.advance(dx < 0 ? 1 : -1);
        }
        this.touch = null;
      }, {passive:true});
      [this.all, this.lightbox].forEach(dialog => {
        this.listen(dialog, 'click', event => {
          if (event.target !== dialog) return;
          const box = dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
        });
        this.listen(dialog, 'close', () => {
          this.hold();
          if (dialog === this.lightbox && this.opener?.isConnected) this.opener.focus({preventScroll:true, focusVisible:!this.pointing});
          this.keyFocus();
        });
      });
      this.listen(document, 'shopify:block:select', event => {
        const card = this.cards.find(item => item.dataset.blockId === event.detail?.blockId);
        if (card) { this.paused = true; this.motion(); card.scrollIntoView({block:'nearest',inline:'center'}); }
      });
      this.listen(document, 'shopify:block:deselect', () => { this.paused = this.atRest(); this.motion(); });
      this.measure(); this.classList.add('is-ready');
    }
    prepareCard(card, index) {
      const [scale, offset] = rhythm[index % rhythm.length];
      card.dataset.rmoOpen = index;
      card.style.setProperty('--rmo-scale', scale);
      card.style.setProperty('--rmo-offset', offset);
      if (this.items[index].closeup) { card.style.setProperty('--rmo-ratio', '1'); card.style.setProperty('--rmo-position', '50% 18%'); }
      const img = card.querySelector('img');
      this.listen(img, 'load', () => img.classList.add('is-loaded'));
      this.listen(img, 'error', () => card.classList.add('is-failed'));
    }
    makeCard(item, index, wall = false) {
      const card = document.createElement('button');
      card.type = 'button'; card.className = 'rmo-card';
      card.setAttribute('aria-label', `Enlarge photo ${index + 1} of ${this.items.length}`);
      const img = document.createElement('img');
      img.alt = item.alt; img.width = 600; img.height = 900;
      img.decoding = 'async'; img.draggable = false; img.dataset.src = item.thumbnail;
      card.append(img); this.prepareCard(card, index);
      if (wall) { img.loading = 'lazy'; this.load(img); }
      return card;
    }
    load(img) { if (!img.hasAttribute('src')) img.src = img.dataset.src; }
    measure() {
      this.cycle = (this.track.children[this.items.length]?.offsetLeft ?? this.track.scrollWidth) - (this.track.children[0]?.offsetLeft ?? 0);
      this.position = null;
    }
    atRest() { return this.reduce.matches || this.dataset.autoplay !== 'true'; }
    keyFocus() {
      const active = document.activeElement;
      this.focused = !this.pointing && this.strip.contains(active) && active.matches(':focus-visible');
      this.motion();
    }
    canMove() { return this.visible && !document.hidden && !this.paused && !this.hover && !this.focused && !this.drag && !this.all.open && !this.lightbox.open && performance.now() >= this.manualUntil && this.cycle > 0; }
    motion() {
      if (!this.canMove()) {
        cancelAnimationFrame(this.raf); this.raf = 0; this.last = 0; this.position = null;
      } else if (!this.raf) this.raf = requestAnimationFrame(time => this.frame(time));
    }
    frame(time) {
      this.raf = 0;
      if (!this.canMove()) { this.motion(); return; }
      const delta = this.last ? Math.min(time - this.last, 50) : 0;
      this.last = time;
      this.position = ((this.position ?? this.strip.scrollLeft) + delta * .018) % this.cycle;
      this.strip.scrollLeft = this.position;
      this.raf = requestAnimationFrame(next => this.frame(next));
    }
    hold() {
      this.manualUntil = performance.now() + 7000;
      clearTimeout(this.resumeTimer); this.motion();
      this.resumeTimer = setTimeout(() => this.motion(), 7050);
    }
    endDrag() {
      if (!this.drag) return;
      this.drag = null; this.strip.classList.remove('is-dragging'); this.hold();
      clearTimeout(this.clickTimer); this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 0);
    }
    step(direction) {
      this.hold();
      if (direction < 0 && this.strip.scrollLeft < 3 && this.cycle) this.strip.scrollLeft = this.cycle - 1;
      this.strip.scrollBy({left:direction * Math.max(180, this.strip.clientWidth * .72), behavior:this.reduce.matches ? 'auto' : 'smooth'});
    }
    click(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-rmo-open')) {
        if (this.suppressClick) return;
        this.index = Number(button.dataset.rmoOpen); this.opener = button; this.hold(); this.show(); this.lightbox.showModal();
      } else if (button.hasAttribute('data-rmo-all')) {
        this.hold();
        if (!this.gridReady) {
          this.items.forEach((item, index) => this.$('[data-rmo-grid]').append(this.makeCard(item, index, true)));
          this.gridReady = true;
        }
        this.all.showModal();
      } else if (button.hasAttribute('data-rmo-close')) button.closest('dialog').close();
      else if (button.hasAttribute('data-rmo-step')) this.step(Number(button.dataset.rmoStep));
      else if (button.hasAttribute('data-rmo-advance')) this.advance(Number(button.dataset.rmoAdvance));
    }
    show() {
      const item = this.items[this.index];
      if (!item) return;
      const img = this.$('[data-rmo-image]');
      img.src = item.image; img.alt = item.alt;
      this.$('[data-rmo-count]').textContent = `${this.index + 1} / ${this.items.length}`;
    }
    advance(direction) { this.index = (this.index + direction + this.items.length) % this.items.length; this.show(); }
    disconnectedCallback() {
      this.abort?.abort(); this.lazy?.disconnect(); this.resize?.disconnect(); this.approach?.disconnect(); this.visibility?.disconnect();
      cancelAnimationFrame(this.raf); clearTimeout(this.resumeTimer); clearTimeout(this.clickTimer);
      this.all?.close(); this.lightbox?.close();
      this.querySelectorAll('[data-rmo-clone]').forEach(clone => clone.remove());
      this.$('[data-rmo-grid]')?.replaceChildren();
      this.classList.remove('is-ready');
      this.ready = false;
    }
  }
  customElements.define('ruemood-photo-journal', RuemoodPhotoJournal);
})();
